/**
 * 全局错误处理工具类
 * 统一处理应用中的各类错误，提供友好的用户提示
 */

import { logger } from './logger.js'

// 错误类型枚举
export const ErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  API_ERROR: 'API_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
}

// 错误消息映射
const errorMessages = {
  [ErrorType.NETWORK_ERROR]: '网络连接异常，请检查网络后重试',
  [ErrorType.API_ERROR]: '服务器响应异常，请稍后重试',
  [ErrorType.AUTH_ERROR]: '登录状态已过期，请重新登录',
  [ErrorType.VALIDATION_ERROR]: '数据验证失败，请检查输入内容',
  [ErrorType.UNKNOWN_ERROR]: '发生未知错误，请稍后重试'
}

/**
 * 获取错误消息
 */
export function getErrorMessage(errorType) {
  return errorMessages[errorType] || errorMessages[ErrorType.UNKNOWN_ERROR]
}

/**
 * 显示错误提示
 */
export function showErrorToast(message) {
  uni.showToast({
    title: message,
    icon: 'none',
    duration: 3000
  })
}

/**
 * 处理网络错误
 */
export function handleNetworkError(error) {
  logger.error('Network Error:', error)
  showErrorToast(getErrorMessage(ErrorType.NETWORK_ERROR))
}

/**
 * 处理API错误
 */
export function handleApiError(response) {
  const { statusCode, data } = response
  
  logger.error('API Error:', { statusCode, data })
  
  // 401 未授权
  if (statusCode === 401) {
    showErrorToast(getErrorMessage(ErrorType.AUTH_ERROR))
    // 清除登录状态
    uni.removeStorageSync('token')
    uni.removeStorageSync('userInfo')
    // 跳转到登录页
    setTimeout(() => {
      uni.reLaunch({ url: '/pages/login/login' })
    }, 1500)
    return
  }
  
  // 400 参数错误
  if (statusCode === 400) {
    const message = data?.message || getErrorMessage(ErrorType.VALIDATION_ERROR)
    showErrorToast(message)
    return
  }
  
  // 其他错误
  const message = data?.message || getErrorMessage(ErrorType.API_ERROR)
  showErrorToast(message)
}

/**
 * 处理未知错误
 */
export function handleUnknownError(error) {
  logger.error('Unknown Error:', error)
  showErrorToast(getErrorMessage(ErrorType.UNKNOWN_ERROR))
}

/**
 * 统一错误处理入口
 */
export function handleError(error) {
  if (!error) return
  
  // 忽略阿里云播放器的容器元素错误（移动端Web常见）
  if (error.message && error.message.includes('Cannot set properties of null') &&
      error.message.includes('innerHTML')) {
    console.warn('Aliyun player container error (ignored):', error.message);
    return;
  }
  
  // 忽略阿里云播放器的 style 属性错误（移动端Web常见）
  if (error.message && error.message.includes('Cannot read properties of null') &&
      error.message.includes('style')) {
    console.warn('Aliyun player style error (ignored):', error.message);
    return;
  }
  
  // 忽略阿里云播放器的视频资源403错误（CDN配置问题）
  if (error.message && error.message.includes('403') &&
      error.message.includes('Forbidden') &&
      (error.message.includes('vod.slash99.com') || error.message.includes('aliplayer'))) {
    console.warn('Aliyun player video 403 error (ignored):', error.message);
    return;
  }
  
  // 网络错误
  if (error.errMsg && error.errMsg.includes('fail') && 
      (error.errMsg.includes('network') || error.errMsg.includes('timeout'))) {
    handleNetworkError(error)
    return
  }
  
  // API响应错误
  if (error.statusCode) {
    handleApiError(error)
    return
  }
  
  // 其他错误
  handleUnknownError(error)
}

/**
 * 包装Promise，自动处理错误
 */
export function withErrorHandling(promise, options = {}) {
  const { showToast = true } = options
  
  return promise
    .then(response => {
      // 如果是API响应，检查状态码
      if (response && response.statusCode && response.statusCode >= 400) {
        if (showToast) {
          handleApiError(response)
        }
        return Promise.reject(response)
      }
      return response
    })
    .catch(error => {
      if (showToast) {
        handleError(error)
      }
      return Promise.reject(error)
    })
}
